// Shared building blocks for in-game control-hint banners.
//
// TutorialHintBanner and PauseHintBanner both render the same keycap +
// label visual language (white rounded card, depress-on-press keys,
// gamepad SVG glyphs). The CSS, glyphs, and held-state logic live here
// so both consumers share one stylesheet mount and identical rendering.

import { t } from '../i18n';
import { InputBtn } from '../core/InputFrame';
import type { InputModality } from '../core/Input';

export type ControlName =
  | 'move' | 'sprint' | 'jump'
  | 'grab' | 'punch' | 'kick' | 'headbutt' | 'throw'
  | 'menu';

export interface InputSample {
  keys: Set<string>;
  mouseL: boolean;
  mouseR: boolean;
  gpButtons: number;
  gpStickActive: boolean;
  gpStartHeld: boolean;
}

const STYLE_ID = 'thb-style';

export function ensureControlHintStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLE_BLOCK;
  document.head.appendChild(style);
}

export function pressed(pad: globalThis.Gamepad, idx: number, threshold = 0.5): boolean {
  const b = pad.buttons[idx];
  if (!b) return false;
  return b.pressed || b.value > threshold;
}

export function labelFor(name: ControlName, modality: InputModality): string {
  const flavor = modality === 'touch' ? 'touch' : 'kbm';
  return t(`tutorial.binding.${name}.${flavor}`);
}

export function isControlHeld(name: ControlName, modality: InputModality, s: InputSample): boolean {
  if (modality === 'kbm') {
    switch (name) {
      case 'move':     return s.keys.has('KeyW') || s.keys.has('KeyA') || s.keys.has('KeyS') || s.keys.has('KeyD');
      case 'sprint':   return s.keys.has('ShiftLeft') || s.keys.has('ShiftRight');
      case 'jump':     return s.keys.has('Space');
      case 'grab':     return s.keys.has('KeyE');
      case 'punch':    return s.mouseL;
      case 'kick':
      case 'headbutt': return s.mouseR;
      case 'throw':    return s.keys.has('KeyQ');
      case 'menu':     return s.keys.has('KeyP') || s.keys.has('Escape');
    }
  }
  if (modality === 'gamepad') {
    switch (name) {
      case 'move':     return s.gpStickActive;
      case 'sprint':   return (s.gpButtons & InputBtn.Sprint) !== 0;
      case 'jump':     return (s.gpButtons & InputBtn.Jump) !== 0;
      case 'grab':     return (s.gpButtons & InputBtn.Grab) !== 0;
      case 'punch':    return (s.gpButtons & InputBtn.Punch) !== 0;
      case 'kick':
      case 'headbutt': return (s.gpButtons & InputBtn.JumpKick) !== 0;
      case 'throw':    return (s.gpButtons & InputBtn.Punch) !== 0;
      case 'menu':     return s.gpStartHeld;
    }
  }
  if (modality === 'touch') {
    if (name === 'move') {
      return document.querySelector('#mobile-controls .mc-joy-base.active') !== null;
    }
    if (name === 'menu') {
      return false;
    }
    const sel =
      name === 'sprint' ? '[data-action="sprint"]' :
      name === 'jump'   ? '[data-action="jump"]' :
      name === 'grab'   ? '[data-action="grab"]' :
      name === 'punch' || name === 'throw' ? '[data-action="punch"]' :
      name === 'kick' || name === 'headbutt' ? '[data-action="kick"]' :
      null;
    if (!sel) return false;
    return document.querySelector(`#mobile-controls ${sel}.pressed`) !== null;
  }
  return false;
}

export function sampleInputState(
  modality: InputModality,
  keys: Set<string>,
  mouseL: boolean,
  mouseR: boolean
): InputSample {
  const sample: InputSample = {
    keys, mouseL, mouseR,
    gpButtons: 0,
    gpStickActive: false,
    gpStartHeld: false
  };

  if (modality === 'gamepad') {
    const pads = navigator.getGamepads?.() ?? [];
    let buttons = 0;
    let stickActive = false;
    let startHeld = false;
    for (const p of pads) {
      if (!p || !p.connected) continue;
      if (pressed(p, 7, 0.5)) buttons |= InputBtn.Punch;
      if (pressed(p, 6, 0.5)) buttons |= InputBtn.Grab;
      if (pressed(p, 0))      buttons |= InputBtn.Jump;
      if (pressed(p, 2))      buttons |= InputBtn.Sprint;
      if (pressed(p, 1))      buttons |= InputBtn.JumpKick;
      if (pressed(p, 9))      startHeld = true;
      const lx = p.axes[0] ?? 0;
      const ly = p.axes[1] ?? 0;
      if (Math.hypot(lx, ly) > 0.18) stickActive = true;
    }
    sample.gpButtons = buttons;
    sample.gpStickActive = stickActive;
    sample.gpStartHeld = startHeld;
  }

  return sample;
}

// --- gamepad SVG glyphs --------------------------------------------------
//
// One SVG per ControlName. Single design for both Xbox and PlayStation;
// face buttons use Xbox letters since the StandardMapping is Xbox-flavored.

const GP_BG = '#2c3242';
const GP_BG_DARK = '#1c2030';

export function gamepadGlyph(name: ControlName): string {
  switch (name) {
    case 'move':     return svgStick('L');
    case 'jump':     return svgFaceButton('A', '#5fb95b', '#3e8a3a');
    case 'kick':
    case 'headbutt': return svgFaceButton('B', '#d24a4a', '#9c2a2a');
    case 'sprint':   return svgTrigger('LT');
    case 'grab':     return svgTrigger('RT');
    case 'punch':    return svgFaceButton('X', '#3a7adb', '#1f4ea0');
    case 'throw':    return svgFaceButton('X', '#3a7adb', '#1f4ea0');
    case 'menu':     return svgPill('Start');
  }
}

function svgFaceButton(letter: string, fill: string, stroke: string): string {
  return `
    <svg viewBox="0 0 36 36" width="32" height="32" aria-hidden="true">
      <circle cx="18" cy="18" r="15" fill="${fill}" stroke="${stroke}" stroke-width="2"/>
      <text x="18" y="24" font-family="Chewy, system-ui, sans-serif" font-size="18"
            text-anchor="middle" fill="white" stroke="${stroke}" stroke-width="0.4">${letter}</text>
    </svg>
  `.trim();
}

function svgTrigger(label: string): string {
  return `
    <svg viewBox="0 0 48 30" width="44" height="28" aria-hidden="true">
      <rect x="3" y="9" width="42" height="18" rx="6" fill="${GP_BG}" stroke="${GP_BG_DARK}" stroke-width="2"/>
      <rect x="9" y="2" width="30" height="9" rx="3" fill="#5a6376" stroke="${GP_BG_DARK}" stroke-width="1.5"/>
      <text x="24" y="22" font-family="Chewy, system-ui, sans-serif" font-size="13"
            text-anchor="middle" fill="white">${label}</text>
    </svg>
  `.trim();
}

function svgStick(label: 'L' | 'R'): string {
  return `
    <svg viewBox="0 0 36 36" width="32" height="32" aria-hidden="true">
      <circle cx="18" cy="18" r="15" fill="${GP_BG}" stroke="${GP_BG_DARK}" stroke-width="2"/>
      <circle cx="18" cy="18" r="9" fill="#5a6376" stroke="${GP_BG_DARK}" stroke-width="1.5"/>
      <text x="18" y="22" font-family="Chewy, system-ui, sans-serif" font-size="11"
            text-anchor="middle" fill="white">${label}</text>
    </svg>
  `.trim();
}

function svgPill(label: string): string {
  return `
    <svg viewBox="0 0 56 24" width="48" height="22" aria-hidden="true">
      <rect x="2" y="2" width="52" height="20" rx="10" fill="${GP_BG}" stroke="${GP_BG_DARK}" stroke-width="2"/>
      <text x="28" y="17" font-family="Chewy, system-ui, sans-serif" font-size="12"
            text-anchor="middle" fill="white">${label}</text>
    </svg>
  `.trim();
}

const STYLE_BLOCK = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#tutorial-hint-banner,
#tutorial-hint-skip,
#match-hint-pause {
  position: fixed;
  z-index: 12;
  pointer-events: none;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  max-width: calc(100vw - 32px);
  display: flex;
  justify-content: center;
}

#tutorial-hint-banner {
  top: calc(16px + env(safe-area-inset-top, 0px));
  left: 50%;
  transform: translateX(-50%);
}

#tutorial-hint-skip.thb-skip-pos {
  /* Sit below the main banner so a 3-row combat block + skip hint don't
   * collide. The two stack vertically; skip tracks the main banner with a
   * fixed 12 px gap. */
  top: calc(20px + env(safe-area-inset-top, 0px));
  right: calc(16px + env(safe-area-inset-right, 0px));
  left: auto;
  transform: none;
  justify-content: flex-end;
}

#match-hint-pause.thb-pause-pos {
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  left: calc(16px + env(safe-area-inset-left, 0px));
  right: auto;
  top: auto;
  transform: none;
  justify-content: flex-start;
}

.thb-card {
  background: rgba(248, 250, 253, 0.96);
  color: #4a5568;
  border-radius: 18px;
  padding: 12px 22px;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.18), 0 1px 0 rgba(255, 255, 255, 0.6) inset;
  font-size: 22px;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  /* Fade transition — fadeUntil math in tick() matches FADE_MS. */
  transition: opacity 280ms ease;
  will-change: opacity;
}

.thb-card.thb-stack {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 12px 18px;
}

.thb-card-small {
  font-size: 16px;
  padding: 8px 14px;
  background: rgba(248, 250, 253, 0.88);
  color: #5a6376;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16);
  gap: 8px;
}

.thb-title {
  font-size: 18px;
  color: #6b7280;
  text-align: center;
  margin-bottom: 2px;
  white-space: nowrap;
}

.thb-row {
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.thb-row-body {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

/* Keycap — fixed height + border-box so the .held state can swap colors
 * without changing layout box dimensions. The press-down feel comes from
 * a transform translateY (which doesn't affect siblings) plus a brightness
 * shift; no border or padding is animated. */
.thb-key {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  background: #d8e4ff;
  color: #2752c8;
  border: 2px solid #b9caf2;
  /* The "lip" that suggests depth lives on box-shadow, not border-bottom-
   * width. Constant geometry → no layout shift on press. */
  box-shadow: 0 3px 0 rgba(31, 78, 160, 0.18);
  font-size: 18px;
  letter-spacing: 0.04em;
  transition: transform 80ms ease, background 80ms ease,
              border-color 80ms ease, box-shadow 80ms ease, color 80ms ease;
}

.thb-card-small .thb-key {
  min-width: 30px;
  height: 28px;
  padding: 0 8px;
  font-size: 14px;
  box-shadow: 0 2px 0 rgba(31, 78, 160, 0.18);
}

.thb-key.held {
  background: #2752c8;
  color: #ffffff;
  border-color: #1b3d9c;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.25);
  transform: translateY(2px);
}

/* Gamepad-glyph keycap: the SVG carries its own colors; the cap container
 * is a near-transparent frame so the SVG dominates. Held state is a soft
 * glow + press translate — same press feel as text caps. */
.thb-key.thb-key-gp {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  padding: 0 4px;
  min-width: 40px;
  height: 36px;
}
.thb-key.thb-key-gp svg {
  display: block;
  filter: drop-shadow(0 2px 0 rgba(0, 0, 0, 0.18));
  transition: transform 80ms ease, filter 80ms ease;
}
.thb-key.thb-key-gp.held {
  background: transparent;
  box-shadow: none;
  transform: none;
}
.thb-key.thb-key-gp.held svg {
  transform: translateY(2px);
  filter: drop-shadow(0 0 4px rgba(255, 220, 120, 0.9))
          drop-shadow(0 1px 0 rgba(0, 0, 0, 0.22));
}

.thb-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: auto;
  padding-left: 8px;
  font-size: 22px;
  color: #b6c0cc;
  transition: color 120ms ease, transform 120ms ease;
}

.thb-check.on {
  color: #2dbe6e;
  transform: scale(1.1);
}

@media (max-width: 540px) {
  .thb-card {
    font-size: 18px;
    padding: 10px 16px;
  }
  .thb-title { font-size: 15px; }
  .thb-key {
    min-width: 32px;
    height: 30px;
    font-size: 14px;
    padding: 0 8px;
  }
  .thb-card-small {
    font-size: 14px;
    padding: 6px 10px;
  }
  .thb-card-small .thb-key {
    min-width: 26px;
    height: 24px;
    font-size: 12px;
  }
}
`;
